import React, { useEffect, useCallback } from 'react';
import styles from './HealthDashboardModal.module.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faArrowUp,
  faUsers,
  faCalendarAlt,
} from '@fortawesome/free-solid-svg-icons';

const getTierColor = (tier) => {
  switch (tier) {
    case 'Prime':
      return styles.prime;
    case 'Promising':
      return styles.promising;
    case 'Acceptable':
      return styles.acceptable;
    case 'Mid-Risk':
      return styles.midRisk;
    case 'High-Risk':
      return styles.highRisk;
    default:
      return '';
  }
};

const HealthDashboardModal = ({ isOpen, onClose, data, isLoading }) => {
  // --- THE FIX for Escape Key ---
  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    },
    [onClose],
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }
    // Cleanup function to remove the event listener
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, handleKeyDown]);
  // --- End of Escape Key Fix ---

  if (!isOpen) return null;

  const score = data?.healthScore ?? 0;
  const tier = data?.healthTier ?? 'N/A';
  const rationale = data?.rationale ?? 'Rationale not available.';
  // `dashboard` is now just `data` itself.
  const dashboard = data;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h3>Channel Health Dashboard</h3>
          <button
            onClick={onClose}
            className={styles.closeButton}
            aria-label="Close"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {isLoading ? (
          <div className={styles.loadingContainer}>
            <div className={styles.spinner}></div>
            <p>Loading Health Analysis...</p>
          </div>
        ) : !dashboard ? (
          <div className={styles.errorContainer}>
            <h4>Analysis Not Available</h4>
            <p>
              Detailed health data could not be loaded for this channel. Try to
              generate a new one.
            </p>
          </div>
        ) : (
          <>
            <div className={styles.mainGrid}>
              <div className={styles.scoreCard}>
                <div className={`${styles.tierBadge} ${getTierColor(tier)}`}>
                  {tier}
                </div>
                <div className={styles.scoreValue}>{score.toFixed(2)}</div>
                <div className={styles.scoreLabel}>Total Health Score</div>
                <div className={styles.sizeTier}>
                  {dashboard.influencerSizeTier ?? 'N/A'}
                </div>
              </div>
              <div className={styles.rationaleCard}>
                <h4>Analyst Rationale</h4>
                <p>{rationale}</p>
              </div>
            </div>

            <h4>Performance Breakdown</h4>
            <div className={styles.kpiGrid}>
              <div className={styles.kpiCard}>
                <FontAwesomeIcon icon={faArrowUp} className={styles.kpiIcon} />
                {/* --- THE FIX: Round the score --- */}
                <div className={styles.kpiValue}>
                  {Math.round(dashboard.scoringBreakdown?.growth?.score ?? 0)}/5
                </div>
                <div className={styles.kpiLabel}>Growth & Momentum</div>
                <div className={styles.kpiDetail}>
                  {dashboard.scoringBreakdown?.growth?.value ?? 'N/A'}
                </div>
              </div>
              <div className={styles.kpiCard}>
                <FontAwesomeIcon icon={faUsers} className={styles.kpiIcon} />
                {/* --- THE FIX: Round the score --- */}
                <div className={styles.kpiValue}>
                  {Math.round(dashboard.scoringBreakdown?.audience?.score ?? 0)}
                  /5
                </div>
                <div className={styles.kpiLabel}>Audience Efficiency</div>
                <div className={styles.kpiDetail}>
                  {dashboard.scoringBreakdown?.audience?.value ?? 'N/A'}
                </div>
              </div>
              <div className={styles.kpiCard}>
                <FontAwesomeIcon
                  icon={faCalendarAlt}
                  className={styles.kpiIcon}
                />
                {/* --- THE FIX: Round the score --- */}
                <div className={styles.kpiValue}>
                  {Math.round(dashboard.scoringBreakdown?.activity?.score ?? 0)}
                  /5
                </div>
                <div className={styles.kpiLabel}>Activity & Consistency</div>
                <div className={styles.kpiDetail}>
                  {dashboard.scoringBreakdown?.activity?.value ?? 'N/A'}
                </div>
              </div>
            </div>

            <h4>Recent Benchmarks</h4>
            <div className={styles.benchmarkGrid}>
              <div className={styles.benchmarkCard}>
                {/* --- THE FIX: Round before formatting --- */}
                <div className={styles.benchmarkValue}>
                  {Math.round(
                    dashboard.keyBenchmarks?.averageRecentViews ?? 0,
                  ).toLocaleString()}
                </div>
                <div className={styles.benchmarkLabel}>
                  Average Recent Views
                </div>
              </div>
              <div className={styles.benchmarkCard}>
                {/* --- THE FIX: Round before formatting --- */}
                <div className={styles.benchmarkValue}>
                  {Math.round(
                    dashboard.keyBenchmarks?.averageRecentLikes ?? 0,
                  ).toLocaleString()}
                </div>
                <div className={styles.benchmarkLabel}>
                  Average Recent Likes
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default HealthDashboardModal;
